import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Inventory2 as Inventory2Icon,
} from '@mui/icons-material';
import type { StockMatch } from '../../types/stock';

interface StockListProps {
  stocks: StockMatch[];
  selectedStock: string | null;
  onSelectStock: (stockId: string) => void;
}

const getMatchColor = (score: number) => {
  if (score >= 90) return 'success';
  if (score >= 75) return 'warning';
  return 'default';
};

const getMatchBgColor = (score: number) => {
  if (score >= 90) return alpha('#4caf50', 0.06);
  if (score >= 75) return alpha('#ff9800', 0.06);
  return alpha('#9e9e9e', 0.04);
};

const StockList: React.FC<StockListProps> = ({
  stocks,
  selectedStock,
  onSelectStock,
}) => {
  return (
    <Stack
      spacing={0.75}
      sx={{
        maxHeight: 180,
        overflowY: 'auto',
        pr: 0.5,
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'grey.100',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'grey.400',
          borderRadius: 3,
          '&:hover': {
            bgcolor: 'grey.500',
          },
        },
      }}>
      {stocks.map((stock: StockMatch) => (
        <Card
          key={stock.stockId}
          variant="outlined"
          sx={{
            cursor: 'pointer',
            flexShrink: 0,
            borderColor:
              selectedStock === stock.stockId ? 'primary.main' : 'divider',
            borderWidth: selectedStock === stock.stockId ? 2 : 1,
            bgcolor:
              selectedStock === stock.stockId
                ? alpha('#1976d2', 0.04)
                : getMatchBgColor(stock.similarityScore),
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: 'primary.light',
              bgcolor: alpha('#1976d2', 0.06),
            },
          }}
          onClick={e => {
            e.stopPropagation();
            onSelectStock(stock.stockId);
          }}>
          <CardContent
            sx={{
              py: 0.75,
              px: 1.5,
              '&:last-child': { pb: 0.75 },
            }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between">
              <Stack
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{ flex: 1, minWidth: 0 }}>
                {selectedStock === stock.stockId ? (
                  <CheckCircleIcon
                    color="primary"
                    sx={{ fontSize: 18, mt: 0.25 }}
                  />
                ) : (
                  <Inventory2Icon
                    color="action"
                    sx={{ fontSize: 18, opacity: 0.5, mt: 0.25 }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}>
                    <Typography
                      fontWeight="medium"
                      fontSize="0.85rem"
                      lineHeight={1.3}
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}>
                      {stock.stockName}
                    </Typography>
                    {stock.stockUnit && (
                      <Chip
                        label={`Unit: ${stock.stockUnit}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.65rem',
                          height: 20,
                          borderColor: alpha('#1976d2', 0.3),
                          color: 'text.secondary',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Stack>
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {stock.vendorName && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontSize="0.7rem">
                        Vendor: {stock.vendorName}
                      </Typography>
                    )}
                    {stock.companyName && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontSize="0.7rem">
                        Company: {stock.companyName}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
              <Chip
                label={`${stock.similarityScore}%`}
                size="small"
                color={getMatchColor(stock.similarityScore)}
                sx={{
                  fontWeight: 'bold',
                  minWidth: 44,
                  height: 22,
                  fontSize: '0.7rem',
                  ml: 1,
                  flexShrink: 0,
                }}
              />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default StockList;
